import { useState } from 'react';
import { Box } from '@mui/material';
import {
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  formListValueFormatter,
  FormSearchResultAdvanced,
  useDataProvider,
  useLocalization,
  useStore,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import {
  IconLibrary,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';
import { email, enabled } from './accessorFunctions';
import listSchema from './list.json';
import searchSchema from './search.json';
import validationSchema from './validations';
import ServiceProviderLayout from '../../../components/ServiceProviderLayout';
import {
  useServiceProvider,
  withServiceProvider,
} from '../../../../../providers/ServiceProviderProvider';
import getSPLevelStackableButtons from '../../../utils/getSPLevelStackableButtons';

function UserList() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const store = useStore();
  const { serviceProviderId } = useServiceProvider();
  const [resultsFound, setResultsFound] = useState(0);
  const [showSearch, setShowSearch] = useState();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);
  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/users?companyId=${serviceProviderId}`;

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_USER',
        defaultMessage: 'Create User',
      }),
      Icon: IconLibrary.User,
      permissionName: 'CAN_CREATE_SP_USER',
      variant: 'outlined',
      onClick: () => {
        store.domain.list.reset('ServiceProviderUserList');
        navigate(`/serviceproviders/edit/${serviceProviderId}/users/create`);
      },
    },
    {
      permissionName: 'CAN_SEARCH_SP_USER',
      label: !showSearch
        ? formatMessage({
            id: 'SHOW_SEARCH',
            defaultMessage: 'Show Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH',
            defaultMessage: 'Hide Search',
          }),
      Icon: showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data => {
    const { id: idArg, status: statusArg, ...other } = data;
    return Object.fromEntries(
      Object.entries({
        status: statusArg === 'true',
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set('ServiceProviderUserList', searchState);
  };

  const preProcessSearchValues = searchedParams => {
    const formatterMap = {};
    return formListValueFormatter({ data: searchedParams, formatterMap });
  };

  const searchValueFormatter = ({ name: searchName, value }) => {
    switch (searchName) {
      case 'enabled':
        return value === 'true' ? 'Enabled' : 'Disabled';
      default:
        return null;
    }
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'ServiceProviderUserList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      email,
      enabled,
    },
    accessorFunctionsAdditional: {
      navigate: recordId => {
        return `/serviceproviders/edit/${serviceProviderId}/users/${recordId}`;
      },
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    sortBy: 'email',
    sortOrder: 'ASC',
  };

  const searchProps = {
    customComponents: FormFields,
    IconLibrary,
    schema: searchSchema,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset(listProps.storeName);
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    storeName: listProps.storeName,
    initialValues: {
      status: '',
    },
    getValueControl: getDefaultValueByControl,
  };

  const searchParamsProps = {
    preProcessSearchValues,
    schema: searchSchema,
    resultsFound,
    valueFormatter: searchValueFormatter,
    storeName: listProps.storeName,
  };

  return (
    <ServiceProviderLayout
      selectedStepperId="USERS"
      stackablePageButtons={getSPLevelStackableButtons({
        navigate,
      })}>
      <PageSection
        HeaderContent={
          <StackablePageButtons
            backButton={false}
            buttons={stackablePageButtons}
          />
        }
        label={formatMessage({
          id: 'SERVICE_PROVIDER_USERS',
          defaultMessage: 'Service Provider Users',
        })}>
        <Grid2 container direction="column" spacing={2}>
          <Grid2 xs={12}>
            <FormSearchResultAdvanced {...searchParamsProps} />
          </Grid2>
          {showSearch && (
            <PageSection
              subSection
              containerProps={{ sx: { mb: 3 } }}
              label={formatMessage({
                id: 'SEARCH_USERS',
                defaultMessage: 'Search Users',
              })}>
              <Box>
                <CRUDComponent
                  type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
                  searchProps={searchProps}
                />
              </Box>
            </PageSection>
          )}

          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
              listProps={listProps}
            />
          </Box>
        </Grid2>
      </PageSection>
    </ServiceProviderLayout>
  );
}

const UserListObservable = observer(UserList);
export default withServiceProvider(UserListObservable);
